import CambioPasswordPaso1 from "./CambioPasswordPaso1";
import CambioPasswordPaso2 from "./CambioPasswordPaso2";
import CambioPasswordPaso3 from "./CambioPasswordPaso3";
import { useEffect, useState } from "react";
import styled from "styled-components";
import TecladoAlfanumerico from "_+_Portal/components/Genericos/TecladoAlfanumerico/TecladoAlfanumerico";

// Contenedor principal (migrado desde CSS)
const Container = styled.div`
  width: 100%;
  margin: 0 auto;
`;

// Wrapper del contenido superior (empareja ancho con el teclado)
const ModalContentWrapper = styled.div`
  width: 100%;
  max-width: 1040px;
  margin: 0 auto;
`;

// Wrapper del teclado (mismo diseño que PedirClave)
const TecladoWrapper = styled.div`
  width: 100%;
  padding: 20px 0;
  background-color: var(--color-latex10);
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const TecladoInner = styled.div`
  width: 1040px;
  max-width: 95vw;
  overflow: visible;
`;

/**
 * Modal de Cambio de Password (contenedor)
 * - Responsive (mobile/tablet/desktop)
 * - Usa styled-components en lugar de clases CSS
 */
const CambioPassword = ({
  texto,
  dissmiss,
  pathModal,
  pasoModal,
  navigateTo,
}) => {
  const [paso, setPaso] = useState(1);

  // Mostrar teclado solo en tablet/desktop
  const [isTabletOrDesktop, setIsTabletOrDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  useEffect(() => {
    const onResize = () => setIsTabletOrDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Trackea el input activo por foco, para dirigir la escritura del teclado
  const [activeInputName, setActiveInputName] = useState("");

  // Orden de preferencia para detectar un input existente
  const preferredInputNames = [
    "nro_documento",
    "codigoSeguridad",
    "txtNuevoPassword",
    "txtRepitoPassword",
  ];

  const findFirstAvailableInputName = () => {
    // 1) Si el elemento activo es un INPUT con name válido, usarlo
    const ae = document.activeElement;
    if (ae && ae.tagName === "INPUT" && ae.getAttribute("name")) {
      return ae.getAttribute("name");
    }
    // 2) Buscar en el DOM por orden de preferencia (sin chequear visibilidad)
    for (const nm of preferredInputNames) {
      const el = document.getElementsByName(nm)[0];
      if (el) return nm;
    }
    return "";
  };

  useEffect(() => {
    const onFocusIn = (e) => {
      const target = e.target;
      if (target && target.tagName === "INPUT" && target.name) {
        setActiveInputName(target.name);
      }
    };
    document.addEventListener("focusin", onFocusIn);
    // Fijar un input activo inicial por si el focus ocurrió antes de montar el listener
    const initial = findFirstAvailableInputName();
    if (initial) setActiveInputName(initial);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, []);

  // Inputs que solo aceptan números desde el teclado (DNI y contraseñas numéricas)
  const numericOnlyNames = new Set([
    "nro_documento",
    "txtNuevoPassword",
    "txtRepitoPassword",
  ]);

  const sanitizeForActiveInput = (value) => {
    const nameToUse = activeInputName || findFirstAvailableInputName();
    if (numericOnlyNames.has(nameToUse)) {
      return (value || "").replace(/[^0-9]/g, "");
    }
    return value || "";
  };

  // Helpers para actualizar el input DOM y disparar eventos controlados
  const updateActiveInputValue = (newValue) => {
    let nameToUse = activeInputName || findFirstAvailableInputName();
    if (!nameToUse) return;
    const el = document.getElementsByName(nameToUse)[0];
    if (!el) return;
    const maxLen = typeof el.maxLength === "number" ? el.maxLength : -1;
    const finalValue =
      maxLen > -1 && newValue.length > maxLen
        ? newValue.slice(0, maxLen)
        : newValue;
    if (document.activeElement !== el) {
      el.focus();
    }
    el.value = finalValue;
    // InputEvent para asegurar onChange de React en inputs controlados
    try {
      const inputEvt = new InputEvent("input", { bubbles: true });
      el.dispatchEvent(inputEvt);
    } catch (e) {
      el.dispatchEvent(new Event("input", { bubbles: true }));
    }
    el.dispatchEvent(new Event("change", { bubbles: true }));
    // Mantener referencia del activo por si no existía
    if (!activeInputName) setActiveInputName(nameToUse);
  };

  const deleteFromActiveInput = () => {
    let nameToUse = activeInputName || findFirstAvailableInputName();
    if (!nameToUse) return;
    const el = document.getElementsByName(nameToUse)[0];
    if (!el) return;
    const current = el.value || "";
    updateActiveInputValue(current.slice(0, -1));
  };

  // Handlers del teclado (usan el input activo)
  const handleKeyboardChange = (value) => {
    if (typeof value !== "string") return;
    const sanitized = sanitizeForActiveInput(value);
    updateActiveInputValue(sanitized);
  };
  const handleKeyboardDelete = () => {
    deleteFromActiveInput();
  };

  const handleKeyboardEnter = () => {
    // Solo en Paso1: simulo click en el botón "Siguiente" si existe
    const btn = document.getElementById("btnSiguienteCambioPassword");
    if (btn) btn.click();
  };

  return (
    <>
      <ModalContentWrapper>
        <Container>
          {paso === 1 && (
            <CambioPasswordPaso1
              titulo="Recuperá tu contraseña"
              dissmiss={dissmiss}
              pathModal={pathModal}
              pasoModal={pasoModal}
              navigateTo={navigateTo}
            />
          )}
          {paso === 2 && (
            <CambioPasswordPaso2
              titulo="Ingresá el código de seguridad"
              texto={texto}
              dissmiss={dissmiss}
              pathModal={pathModal}
              pasoModal={pasoModal}
              navigateTo={navigateTo}
            />
          )}
          {paso === 3 && (
            <CambioPasswordPaso3
              titulo="Creá una contraseña nueva"
              texto={texto}
              dissmiss={dissmiss}
              pathModal={pathModal}
              pasoModal={pasoModal}
              navigateTo={navigateTo}
            />
          )}
        </Container>
      </ModalContentWrapper>

      {isTabletOrDesktop && (
        <TecladoWrapper>
          <TecladoInner>
            <TecladoAlfanumerico
              sinNumeros={false}
              modeLight={true}
              onEnter={handleKeyboardEnter}
              enterStr={"Enter"}
              onChange={handleKeyboardChange}
              maxLength={20}
              onDelete={deleteFromActiveInput}
            />
          </TecladoInner>
        </TecladoWrapper>
      )}
    </>
  );
};

export default CambioPassword;
